export interface RgbColor {
  b: number
  g: number
  r: number
}

interface PlatePoint {
  u: number
  v: number
}

export interface ChladniPreset {
  m: number
  n: number
}

interface SliceAngleRange {
  span: number
  startAngle: number
}

export interface BackgroundParticle {
  amp: number
  anchorU: number
  anchorV: number
  chaosU: number
  chaosV: number
  frequencyA: number
  frequencyB: number
  fromAnchorU: number
  fromAnchorV: number
  fromNodalStrength: number
  fromSliceIndex: number
  nodalStrength: number
  phaseA: number
  phaseB: number
  scatterU: number
  scatterV: number
  sliceIndex: number
}

const GOLDEN_ANGLE_RADIANS = Math.PI * (3 - Math.sqrt(5))
export const PLATE_DOMAIN_RADIUS = 0.49
const CHLADNI_FREQUENCY_SCALE = 0.65
const CHLADNI_EQUAL_AXIS_EPSILON = 1e-6

export function clampNumber(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value))
}

function normalizeSetSliceAngle(angle: number) {
  const fullCircle = Math.PI * 2
  let normalized = angle % fullCircle
  if (normalized < 0) {
    normalized += fullCircle
  }
  return normalized
}

function resolveSetSliceAngle(platePoint: PlatePoint) {
  const clamped = clampToPlate(platePoint.u, platePoint.v)
  return normalizeSetSliceAngle(Math.atan2(0.5 - clamped.v, clamped.u - 0.5))
}

function resolveSetSliceIndexForAngle(
  angle: number,
  slices: SliceAngleRange[],
) {
  const normalizedAngle = normalizeSetSliceAngle(angle)
  for (let index = 0; index < slices.length; index += 1) {
    const slice = slices[index]
    const delta = normalizeSetSliceAngle(normalizedAngle - slice.startAngle)
    if (delta <= slice.span + 1e-6) {
      return index
    }
  }
  return Math.max(0, slices.length - 1)
}

const PLATE_SCREEN_DIAMETER_RATIO = 0.88

function getPlateGeometry(width: number, height: number) {
  const diameter = Math.max(
    1,
    Math.min(width, height) * PLATE_SCREEN_DIAMETER_RATIO,
  )
  return {
    centerX: width / 2,
    centerY: height / 2,
    diameter,
  }
}

export function projectToScreen(
  u: number,
  v: number,
  width: number,
  height: number,
) {
  const plate = getPlateGeometry(width, height)
  return {
    x: plate.centerX + (u - 0.5) * plate.diameter,
    y: plate.centerY + (0.5 - v) * plate.diameter,
  }
}

export function clampToPlate(u: number, v: number): PlatePoint {
  const clampedU = clampNumber(u, 0, 1)
  const clampedV = clampNumber(v, 0, 1)
  const dx = clampedU - 0.5
  const dy = clampedV - 0.5
  const radius = Math.hypot(dx, dy)
  if (radius <= PLATE_DOMAIN_RADIUS) {
    return { u: clampedU, v: clampedV }
  }
  const scale = PLATE_DOMAIN_RADIUS / Math.max(radius, 1e-6)
  return {
    u: clampNumber(0.5 + dx * scale, 0, 1),
    v: clampNumber(0.5 + dy * scale, 0, 1),
  }
}

function hashNumber(value: number) {
  let hash = Math.imul(value ^ 0x9e3779b9, 0x85ebca6b)
  hash ^= hash >>> 13
  hash = Math.imul(hash, 0xc2b2ae35)
  hash ^= hash >>> 16
  return hash >>> 0
}

function unitFloatFromHash(hash: number) {
  return hash / 0xffffffff
}

function evaluateChladniField(u: number, v: number, preset: ChladniPreset) {
  const domainScale = Math.max(1e-6, PLATE_DOMAIN_RADIUS)
  const x = clampNumber((u - 0.5) / domainScale, -1, 1)
  const y = clampNumber((v - 0.5) / domainScale, -1, 1)
  const scaledM = Math.max(1, preset.m * CHLADNI_FREQUENCY_SCALE)
  let scaledN = Math.max(1, preset.n * CHLADNI_FREQUENCY_SCALE)

  // Guard against degenerate m===n presets that collapse the field to zero.
  if (Math.abs(scaledM - scaledN) < CHLADNI_EQUAL_AXIS_EPSILON) {
    scaledN += 0.5
  }

  return (
    Math.cos(scaledN * Math.PI * x) * Math.cos(scaledM * Math.PI * y) -
    Math.cos(scaledM * Math.PI * x) * Math.cos(scaledN * Math.PI * y)
  )
}

const CHLADNI_GRADIENT_STEP = 0.0025

function _evaluateChladniGradient(u: number, v: number, preset: ChladniPreset) {
  const uMinus = clampNumber(u - CHLADNI_GRADIENT_STEP, 0, 1)
  const uPlus = clampNumber(u + CHLADNI_GRADIENT_STEP, 0, 1)
  const vMinus = clampNumber(v - CHLADNI_GRADIENT_STEP, 0, 1)
  const vPlus = clampNumber(v + CHLADNI_GRADIENT_STEP, 0, 1)

  const gradientU =
    (evaluateChladniField(uPlus, v, preset) -
      evaluateChladniField(uMinus, v, preset)) /
    Math.max(1e-6, uPlus - uMinus)
  const gradientV =
    (evaluateChladniField(u, vPlus, preset) -
      evaluateChladniField(u, vMinus, preset)) /
    Math.max(1e-6, vPlus - vMinus)

  return {
    magnitude: Math.hypot(gradientU, gradientV),
    u: gradientU,
    v: gradientV,
  }
}

function computeBackgroundChaosAnchor(
  index: number,
  totalCount: number,
  hash: number,
) {
  const count = Math.max(1, Math.floor(totalCount))
  const radius = Math.sqrt((index + 0.5) / count) * PLATE_DOMAIN_RADIUS * 0.992
  const baseTheta = index * GOLDEN_ANGLE_RADIANS

  const radialJitter = ((((hash >>> 17) % 10000) / 10000) * 2 - 1) * 0.003
  const angularJitter =
    ((((hash >>> 23) % 10000) / 10000) * 2 - 1) *
    ((Math.PI * 2 * 0.0035) / Math.sqrt(count))

  const jitteredRadius = clampNumber(
    radius + radialJitter,
    0,
    PLATE_DOMAIN_RADIUS,
  )
  const theta = baseTheta + angularJitter
  return clampToPlate(
    0.5 + Math.cos(theta) * jitteredRadius,
    0.5 + Math.sin(theta) * jitteredRadius,
  )
}

export function computeBackgroundScatterAnchor(
  index: number,
  transitionSeed: number,
) {
  const seedHash = hashNumber(
    (index + 1) ^ hashNumber(transitionSeed + 0x68bc21eb),
  )
  const radiusUnit = unitFloatFromHash(hashNumber(seedHash ^ 0xa24baed4))
  const angleUnit = unitFloatFromHash(hashNumber(seedHash ^ 0x9fb21c13))
  const radius = Math.sqrt(radiusUnit) * PLATE_DOMAIN_RADIUS
  const theta = angleUnit * Math.PI * 2

  return clampToPlate(
    0.5 + Math.cos(theta) * radius,
    0.5 + Math.sin(theta) * radius,
  )
}

interface MotionChaosParticle {
  amp: number
  chaosU: number
  chaosV: number
  frequencyA: number
  frequencyB: number
  phaseA: number
  phaseB: number
}

export const IDLE_TRANSITION_REGROUP_LANDING_MIX = 0.985

interface MotionScatterParticle extends MotionChaosParticle {
  scatterU: number
  scatterV: number
}

export function computeBackgroundChaosPoint(
  particle: MotionChaosParticle,
  time: number,
) {
  const pulse = 0.0016 + particle.amp * 0.0026
  return clampToPlate(
    particle.chaosU +
      Math.cos(time * (1.95 + particle.frequencyA) + particle.phaseA) * pulse,
    particle.chaosV +
      Math.sin(time * (2.05 + particle.frequencyB) + particle.phaseB) * pulse,
  )
}

export function computeBackgroundScatterPoint(
  particle: MotionScatterParticle,
  time: number,
) {
  const pulse = 0.0048 + particle.amp * 0.0064
  return clampToPlate(
    particle.scatterU +
      Math.cos(time * (3.15 + particle.frequencyB * 0.8) + particle.phaseB) *
        pulse,
    particle.scatterV +
      Math.sin(time * (3.05 + particle.frequencyA * 0.75) + particle.phaseA) *
        pulse,
  )
}

export function computeIdleAmbientDriftPoint(
  particle: MotionChaosParticle,
  time: number,
) {
  const chaosPoint = computeBackgroundChaosPoint(particle, time)
  const idleJitter = 0.0032 + particle.amp * 0.0028

  return clampToPlate(
    chaosPoint.u +
      Math.cos(time * (1.7 + particle.frequencyA) + particle.phaseA) *
        idleJitter,
    chaosPoint.v +
      Math.sin(time * (1.75 + particle.frequencyB) + particle.phaseB) *
        idleJitter,
  )
}

export function computeIdleAmbientParticlePoint(
  particle: MotionChaosParticle,
  time: number,
) {
  const driftPoint = computeIdleAmbientDriftPoint(particle, time)
  const localJitter = 0.0009 + particle.amp * 0.0012

  return clampToPlate(
    driftPoint.u +
      Math.cos(time * (2.45 + particle.frequencyA) + particle.phaseA) *
        localJitter,
    driftPoint.v +
      Math.sin(time * (2.2 + particle.frequencyB) + particle.phaseB) *
        localJitter,
  )
}

function resolveNodalAnchor(
  index: number,
  totalCount: number,
  hash: number,
  preset: ChladniPreset,
): { nodalStrength: number; point: PlatePoint } {
  const base = computeBackgroundChaosAnchor(index, totalCount, hash)
  let best = base
  let bestFieldAbs = Math.abs(evaluateChladniField(base.u, base.v, preset))

  for (let ring = 1; ring <= 3; ring += 1) {
    const ringRadius = ring * 0.0085
    for (let sample = 0; sample < 8; sample += 1) {
      const sampleTheta =
        index * GOLDEN_ANGLE_RADIANS +
        ring * 0.37 +
        sample * ((Math.PI * 2) / 8) +
        (hash % 31) * 0.01
      const candidate = clampToPlate(
        base.u + Math.cos(sampleTheta) * ringRadius,
        base.v + Math.sin(sampleTheta) * ringRadius,
      )
      const candidateFieldAbs = Math.abs(
        evaluateChladniField(candidate.u, candidate.v, preset),
      )
      if (candidateFieldAbs < bestFieldAbs) {
        best = candidate
        bestFieldAbs = candidateFieldAbs
      }
    }
  }

  const nodalStrength = clampNumber(1 - bestFieldAbs / 0.55, 0, 1)
  return { nodalStrength, point: best }
}

export function buildBackgroundParticles(
  count: number,
  slices: SliceAngleRange[],
  preset: ChladniPreset,
  previousParticles?: BackgroundParticle[] | null,
  transitionSeed = 0,
) {
  const priorParticles = previousParticles ?? []
  const priorParticleCount = priorParticles.length

  return Array.from({ length: count }, (_, index) => {
    const hash = hashNumber(index + 1)
    const nodal = resolveNodalAnchor(index, count, hash, preset)
    const chaosAnchor = computeBackgroundChaosAnchor(index, count, hash)
    const scatterAnchor = computeBackgroundScatterAnchor(index, transitionSeed)
    const sliceIndex = resolveSetSliceIndexForAngle(
      resolveSetSliceAngle(nodal.point),
      slices,
    )
    const fromParticle =
      priorParticleCount > 0
        ? priorParticles[index % priorParticleCount]
        : undefined

    return {
      amp: 0.55 + ((hash >>> 9) % 120) / 180,
      anchorU: nodal.point.u,
      anchorV: nodal.point.v,
      chaosU: chaosAnchor.u,
      chaosV: chaosAnchor.v,
      frequencyA: 0.8 + ((hash >>> 6) % 120) / 100,
      frequencyB: 0.9 + ((hash >>> 13) % 120) / 100,
      fromAnchorU: fromParticle?.anchorU ?? nodal.point.u,
      fromAnchorV: fromParticle?.anchorV ?? nodal.point.v,
      fromNodalStrength: fromParticle?.nodalStrength ?? nodal.nodalStrength,
      fromSliceIndex: fromParticle?.sliceIndex ?? sliceIndex,
      nodalStrength: nodal.nodalStrength,
      phaseA: ((hash >>> 2) % 6280) / 1000,
      phaseB: ((hash >>> 11) % 6280) / 1000,
      scatterU: scatterAnchor.u,
      scatterV: scatterAnchor.v,
      sliceIndex,
    }
  })
}

function parseCssColor(color: string): RgbColor | null {
  const normalized = color.trim().toLowerCase()
  if (!normalized) {
    return null
  }

  if (normalized.startsWith("#")) {
    const hex = normalized.slice(1)
    if (hex.length === 3) {
      const r = Number.parseInt(hex[0] + hex[0], 16)
      const g = Number.parseInt(hex[1] + hex[1], 16)
      const b = Number.parseInt(hex[2] + hex[2], 16)
      return Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)
        ? null
        : { b, g, r }
    }
    if (hex.length === 6) {
      const r = Number.parseInt(hex.slice(0, 2), 16)
      const g = Number.parseInt(hex.slice(2, 4), 16)
      const b = Number.parseInt(hex.slice(4, 6), 16)
      return Number.isNaN(r) || Number.isNaN(g) || Number.isNaN(b)
        ? null
        : { b, g, r }
    }
    return null
  }

  const rgbComponents = normalized.match(/[\d.]+/g)
  if (!rgbComponents || rgbComponents.length < 3) {
    return null
  }

  const [r, g, b] = rgbComponents
    .slice(0, 3)
    .map((component) => clampNumber(Math.round(Number(component)), 0, 255))
  return { b, g, r }
}

function resolveComputedCssColor(color: string, style: CSSStyleDeclaration) {
  const variableMatch = color
    .trim()
    .match(/^var\(\s*(--[\w-]+)\s*(?:,\s*[^)]+)?\)$/)
  if (!variableMatch) {
    return color
  }

  return style.getPropertyValue(variableMatch[1])
}

export function resolveCanvasColorPalette(colors: string[]) {
  const style = getComputedStyle(document.documentElement)
  const parsedPalette = colors.map((color) =>
    parseCssColor(resolveComputedCssColor(color, style)),
  )
  const fallbackColor = parsedPalette.find(
    (color): color is RgbColor => color !== null,
  ) ?? {
    b: 0,
    g: 0,
    r: 0,
  }

  return parsedPalette.map((color) => color ?? fallbackColor)
}
